import React, { memo, useMemo } from "react";
import { Flex, StyleProps, Text, FlexProps } from "@chakra-ui/react";
import styled from "@emotion/styled";
import { useTransactionsStore } from "../../../../store/transactionsStore";
import { EmptyDataMessage } from "../../../atoms/EmptyDataMessage";
import { LoadingIndicator } from "../../../molecules/LoadingIndicator";
import { MainTransactionTableRow } from "./MainTransactionTableRow";

export const mainTransactionTableStyleProps: Record<
  "NeedsReviewColumn",
  StyleProps
> = {
  NeedsReviewColumn: {
    minW: "50px",
    h: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
};

export const MainTransactionsTable = ({
  onClickOrder,
  onSelect,
  isSelected,
}: {
  onSelect: (id: string) => void;
  isSelected: (id: string) => boolean;
  onClickOrder: (id: string) => void;
}) => (
  <>
    {/* -------------------- Table Header --------------------- */}
    <MainTransactionsTableHeader />
    {/* -------------------- Table Rows ----------------------- */}
    <MainTransactionTableRows
      onClickOrder={onClickOrder}
      onSelect={onSelect}
      isSelected={isSelected}
    />
  </>
);

export const MainTransactionsTableHeader = () => (
  <Flex
    p="2px 20px"
    minW="1230px"
    borderWidth="1px 0 1px 0"
    borderColor="white.500"
  >
    {/* ------------ CheckBox -----------------*/}
    <CheckboxColumn />
    {/* ------------ Table Header Row ---------------------------*/}
    <Flex w="100%">
      <Flex minW="100px" w="10%">
        <THeader>Type</THeader>
      </Flex>
      <Flex minW="180px" w="15%">
        <THeader>Date</THeader>
      </Flex>
      <Flex minW="300px" w="30%">
        <THeader>Out</THeader>
      </Flex>
      <Flex minW="300px" w="30%">
        <THeader>In</THeader>
      </Flex>
      <Flex minW="150px" w="15%" justifyContent="flex-end" mr="50px">
        <THeader>Outcome</THeader>
      </Flex>
      <Flex {...mainTransactionTableStyleProps.NeedsReviewColumn}>
        {/* Needs Review Indicator -- doesn't need a header */}
      </Flex>
    </Flex>
    {/* ------------ 3 dots menu -----------------*/}
    {/*<Flex w="50px" />*/}
  </Flex>
);

export const MainTransactionTableRows = memo(
  ({
    onSelect,
    isSelected,
    onClickOrder,
  }: {
    onSelect: (id: string) => void;
    isSelected: (id: string) => boolean;
    onClickOrder: (id: string) => void;
  }) => {
    const groupList = useTransactionsStore((state) => state.groupList);

    const rows = useMemo(
      () =>
        groupList?.map((groupId) => (
          <MainTransactionTableRow
            key={groupId}
            groupId={groupId}
            onSelect={onSelect}
            isSelected={isSelected(groupId)}
            onClickOrder={onClickOrder}
          />
        )),
      [groupList, isSelected, onClickOrder, onSelect]
    );

    if (!groupList) {
      return <LoadingIndicator content="Loading Transactions" />;
    }
    if (groupList && groupList.length === 0) {
      return (
        <Flex w="100%" justifyContent="center" h="85%">
          <EmptyDataMessage content="Add a data source to make the most of your Syla account" />
        </Flex>
      );
    }

    return <>{rows}</>;
  }
);

const THeader = styled(Text)`
  color: #666;
  text-transform: uppercase;
  font-size: 0.75rem;
  font-weight: 500;
`;

export const CheckboxColumn = ({
  children,
  ...props
}: { children?: React.ReactNode } & FlexProps) => (
  <Flex
    w="50px"
    h="100%"
    bgColor="transparent"
    justifyContent="center"
    {...props}
  >
    {children}
  </Flex>
);
